import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { ICity, IPanelUser, IPermission, IRole } from "../../../../interfaces";
import RolesService from "../../../../services/http/services/RolesService";
import CitiesService from "../../../../services/http/services/CitiesService";

export default function usePanelUserForm(
    onSubmit: (user: IPanelUser) => Promise<void>,
    user: IPanelUser | undefined
) {
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(true);
    const [isLoadingCities, setIsLoadingCities] = useState<boolean>(true);
    const [isLoadingRolePermissions, setIsLoadingRolePermissions] = useState<boolean>(false);
    const [roles, setRoles] = useState<Array<IRole>>([]);
    const [cities, setCities] = useState<Array<ICity>>([]);
    const [rolePermissions, setRolePermissions] = useState<Array<IPermission>>([]);
    const [isUpdatePasswordModalVisible, setIsUpdatePasswordModalVisible] = useState<boolean>(false);

    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        watch,
        clearErrors,
    } = useForm<IPanelUser>({
        defaultValues: {
            name: user?.name || "",
            email: user?.email || "",
            role_id: user?.role?.id,
            city_ids: user?.cities?.map((c) => c.id).filter((id): id is number => typeof id === "number") || [],
        },
    });

    const watchedCityIds = watch("city_ids") || [];
    const watchedRoleId = watch("role_id");

    const handleSubmitForm: SubmitHandler<IPanelUser> = async (data) => {
        setIsSubmiting(true);
        try {
            await onSubmit(data);
        } finally {
            setIsSubmiting(false);
        }
    };

    const handleSelectAllCities = useCallback(() => {
        const allIds = cities.map((c) => c.id).filter((id): id is number => typeof id === "number");
        setValue("city_ids", allIds, { shouldValidate: true });
        clearErrors("city_ids");
    }, [cities, setValue, clearErrors]);

    const handleClearAllCities = useCallback(() => {
        setValue("city_ids", [], { shouldValidate: true });
    }, [setValue]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadRoles() {
            try {
                setIsLoadingRoles(true);
                const rolesData = await RolesService.listRoles(controller.signal);
                setRoles(rolesData || []);
            } catch {
            } finally {
                setIsLoadingRoles(false);
            }
        }

        async function loadCities() {
            try {
                setIsLoadingCities(true);
                const citiesList = await CitiesService.listCitiesToConfig(controller.signal);
                const sorted = (citiesList || []).sort((a: ICity, b: ICity) =>
                    `${a.city} - ${a.uf}`.localeCompare(`${b.city} - ${b.uf}`)
                );
                setCities(sorted);

                if (user?.cities && user.cities.length > 0) {
                    const ids = user.cities
                        .map((c) => c.id)
                        .filter((id): id is number => typeof id === "number");
                    setValue("city_ids", ids);
                }
            } catch {
            } finally {
                setIsLoadingCities(false);
            }
        }

        loadRoles();
        loadCities();

        return () => {
            controller.abort();
        };
    }, [user, setValue]);

    const handleOpenUpdatePasswordModal = useCallback(() => {
        setIsUpdatePasswordModalVisible(true);
    }, []);

    const handleCloseUpdatePasswordModal = useCallback(() => {
        setIsUpdatePasswordModalVisible(false);
    }, []);

    const loadRolePermissions = useCallback(async (roleId: number, signal: AbortSignal) => {
        try {
            setIsLoadingRolePermissions(true);
            const role = await RolesService.getRoleById(roleId, signal);
            setRolePermissions(role?.permissions || []);
        } catch {
            setRolePermissions([]);
        } finally {
            setIsLoadingRolePermissions(false);
        }
    }, []);

    useEffect(() => {
        if (!watchedRoleId) {
            setRolePermissions([]);
            return;
        }

        const controller = new AbortController();
        loadRolePermissions(Number(watchedRoleId), controller.signal);

        return () => {
            controller.abort();
        };
    }, [watchedRoleId, loadRolePermissions]);

    return {
        isLoadingRoles,
        isLoadingCities,
        isLoadingRolePermissions,
        roles,
        cities,
        rolePermissions,
        handleSubmitForm,
        handleSubmit,
        control,
        setValue,
        isSubmiting,
        errors,
        watchedCityIds,
        watchedRoleId,
        handleSelectAllCities,
        handleClearAllCities,
        isUpdatePasswordModalVisible,
        handleOpenUpdatePasswordModal,
        handleCloseUpdatePasswordModal,
    };
}